/**
 * IntersectionObserverのデフォルトオプション。
 * @typedef {Object} Options
 * @property {Element|null} root - 視野の基準となる要素。nullの場合はブラウザのビューポート。
 * @property {string} rootMargin - 基準要素周囲の余白（CSSのmarginと同様の値）。
 * @property {number} threshold - 交差が発生するしきい値（0であれば1ピクセルでも交差時に実行）。
 */
const defaultOptions = {
  root: null,
  rootMargin: "-50px",
  threshold: 0.1,
};

/**
 * スクロールに応じた汎用的なアニメーション処理
 *
 * @param {NodeListOf<Element>} targets - 監視対象の要素。指定がない場合、.js-scroll-animationクラスを含む要素が対象となる。
 * @param {Options} [options={}] - IntersectionObserverのオプション設定。
 *
 * @example
 * // デフォルトのターゲット (.js-scroll-animation) を使用し、optionsのみをカスタマイズする場合
 * initObserveAnimation(undefined, {
 *   threshold: 0.5,       // しきい値を50%に設定
 *   rootMargin: "10px",   // 視野のマージンを10pxに設定
 * });
 */
export default function initObserveAnimation(
  targets = document.querySelectorAll("[class*='js-scroll-animation']"),
  options
) {
  const targetsLength = targets.length;

  if (targetsLength === 0) {
    // console.error("initObserveAnimation: Target elements are not found.");
    return;
  }

  const mergedOptions = { ...defaultOptions, ...options };
  const observer = new IntersectionObserver(
    (entries) => handleObserve(entries, observer),
    mergedOptions
  );

  // 初期表示時にビューポート内にある要素を処理
  checkInitialVisibility(targets, observer);

  // 残りの要素を監視
  targets.forEach((target) => observer.observe(target));

  // 1文字ずつテキストをspanに加工
  document.querySelectorAll(".js-split-char").forEach((animatedText) => {
    const text = animatedText.textContent;
    animatedText.innerHTML = "";

    // テキストを1文字ずつ分割して <span> で囲む
    text.split("").forEach((char, index) => {
      const span = document.createElement("span");
      span.textContent = char;
      span.style.setProperty("--char-index", index); // アニメーションの遅延時間用
      animatedText.appendChild(span);
    });
  });
}

/**
 * IntersectionObserverのコールバック関数。
 * 要素がビューポートに入った時にアニメーションクラスを追加し、監視を停止します。
 *
 * @param {IntersectionObserverEntry[]} entries - 監視対象のエントリの配列。
 * @param {IntersectionObserver} observer - IntersectionObserverインスタンス。
 */
function handleObserve(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add("is-animated");
      observer.unobserve(entry.target); // 観察を解除
    }
  });
}

/**
 * 初期化時に、すでにビューポート内にある要素にも.is-animatedを付与する処理。
 *
 * @param {NodeListOf<Element>} targets - 監視対象の要素。
 * @param {IntersectionObserver} observer - IntersectionObserverインスタンス。
 */
function checkInitialVisibility(targets, observer) {
  targets.forEach((target) => {
    const rect = target.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight && rect.bottom > 0; // ビューポート内判定

    if (isInViewport) {
      target.classList.add("is-animated");
      observer.unobserve(target); // すでに処理済みなので監視を解除
    }
  });
}
